import React, { useState } from 'react';
import { TextField, Button, Typography, Box, Alert, Link, Grid, Paper, InputAdornment, IconButton } from '@mui/material';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import LockIcon from '@mui/icons-material/Lock';
import ShieldIcon from '@mui/icons-material/Shield';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';

function ChangePassword() {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    if (password.length < 6) {
        setError("Password must be at least 6 characters");
        return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API_URL}/change-password`, { new_password: password }, {
         headers: { Authorization: `Bearer ${token}` }
      });
      localStorage.removeItem('must_change_password');
      setMessage("Password changed successfully. Redirecting...");
      setTimeout(() => {
          navigate('/');
      }, 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to change password');
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', bgcolor: '#f4f6f9' }}>
      <Grid container sx={{ flexGrow: 1 }}>
        
        {/* Left Side: Welcome Module */}
        <Grid item xs={12} md={5} lg={6} sx={{
            background: 'linear-gradient(135deg, #1e3c72 0%, #2a5298 100%)',
            color: 'white',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            p: { xs: 4, md: 8, lg: 12 },
            position: 'relative',
            overflow: 'hidden'
        }}>
            {/* Decorative BG elements */}
            <Box sx={{ position: 'absolute', top: -100, right: -50, width: 300, height: 300, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
            <Box sx={{ position: 'absolute', bottom: -50, left: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />

            <Box sx={{ position: 'relative', zIndex: 1, maxWidth: 600 }}>
                <Typography variant="h3" fontWeight="900" sx={{ mb: 2, letterSpacing: -0.5, color: '#ffffff' }}>
                    Welcome to Project Management!
                </Typography>
                <Typography variant="h6" sx={{ mb: 4, fontWeight: 400, opacity: 0.9, lineHeight: 1.6, color: '#e2e8f0' }}>
                    Your comprehensive application to manage projects, IT infrastructure, clients, and team tasks seamlessly in one place.
                </Typography>
                
                <Box sx={{ mt: 4, display: 'flex', alignItems: 'flex-start', bgcolor: 'rgba(255,255,255,0.1)', p: 3, borderRadius: 4, backdropFilter: 'blur(10px)', border: '1px solid rgba(255,255,255,0.2)' }}>
                    <MenuBookIcon sx={{ mr: 2, mt: 0.5, color: '#90caf9', fontSize: 32 }} />
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: '#90caf9' }}>
                            Need help getting started?
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9, lineHeight: 1.6, fontSize: '0.95rem' }}>
                            Check out our comprehensive User Guide. You can always find it on your <strong>Dashboard</strong> once you complete setup, or access it directly right now by clicking <Link href="/manual/Pm.html" target="_blank" sx={{ color: 'white', fontWeight: 'bold', textDecorationColor: 'rgba(255,255,255,0.5)', '&:hover': { color: '#e3f2fd' } }}>here</Link>.
                        </Typography>
                    </Box>
                </Box>
            </Box>
        </Grid>

        {/* Right Side: Change Password Form */}
        <Grid item xs={12} md={7} lg={6} sx={{ 
            display: 'flex', 
            flexDirection: 'column', 
            justifyContent: 'center', 
            alignItems: 'center',
            p: { xs: 3, sm: 6, md: 8 }
        }}>
            <Paper elevation={0} sx={{ 
                p: { xs: 4, sm: 6 }, 
                width: '100%', 
                maxWidth: 480, 
                borderRadius: 4, 
                border: '1px solid #e2e8f0', 
                boxShadow: '0 10px 40px rgba(0,0,0,0.04)',
                bgcolor: 'white'
            }}>
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                    <Box sx={{ display: 'inline-flex', p: 2, borderRadius: '50%', bgcolor: '#f0fdf4', color: '#16a34a', mb: 2 }}>
                        <ShieldIcon fontSize="large" sx={{ fontSize: 40 }} />
                    </Box>
                    <Typography component="h1" variant="h4" fontWeight="800" sx={{ color: '#1e293b', mb: 1, letterSpacing: -0.5 }}>
                        Secure Your Account
                    </Typography>
                    <Typography variant="body1" color="textSecondary">
                        For your security, please configure a new password before accessing the system.
                    </Typography>
                </Box>
                
                {message && <Alert severity="success" sx={{ mb: 3, borderRadius: 2, fontWeight: 500 }}>{message}</Alert>}
                {error && <Alert severity="error" sx={{ mb: 3, borderRadius: 2, fontWeight: 500 }}>{error}</Alert>}

                <Box component="form" onSubmit={handleSubmit}>
                    <TextField
                        margin="normal" 
                        required 
                        fullWidth 
                        label="New Password" 
                        type={showPassword ? 'text' : 'password'}
                        value={password} 
                        onChange={(e) => setPassword(e.target.value)}
                        sx={{ mb: 2, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } } }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setShowPassword(!showPassword)} edge="end">
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                    <TextField
                        margin="normal" 
                        required 
                        fullWidth 
                        label="Confirm Password" 
                        type={showPassword ? 'text' : 'password'}
                        value={confirmPassword} 
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 3, bgcolor: '#f8fafc', '& fieldset': { borderColor: '#e2e8f0' } } }}
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon color="action" /></InputAdornment>
                        }}
                    />
                    <Button 
                        type="submit" 
                        fullWidth 
                        variant="contained" 
                        size="large"
                        sx={{ 
                            mt: 2, 
                            py: 1.8, 
                            borderRadius: 3, 
                            fontWeight: 'bold', 
                            fontSize: '1.05rem',
                            bgcolor: '#1e3c72',
                            textTransform: 'none',
                            boxShadow: '0 8px 20px rgba(30, 60, 114, 0.25)',
                            '&:hover': { bgcolor: '#152b53', transform: 'translateY(-1px)' },
                            transition: 'all 0.2s'
                        }}
                    >
                        Update & Continue
                    </Button>
                </Box>
            </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ChangePassword;
